import React, { useState, useEffect, useCallback } from 'react';

function getDefaultConfig() {
  return {
    model: 'Manual',
    maxChargePower: 100,
    consumption: 18,
    batteryCapacity: 50,
    range: 300,
    startBattery: 100,
    arrivalBattery: 20,
    minBatteryStop: 20,
    maxBatteryCharge: 80,
    tableLow: 20,
    tableHigh: 80,
  };
}

function calcRange(batteryCapacity, consumption) {
  if (!consumption || consumption <= 0) return 0;
  return Math.round((batteryCapacity / consumption) * 100);
}

function calcConsumption(batteryCapacity, range) {
  if (!range || range <= 0) return 0;
  return Math.round((batteryCapacity / range) * 100 * 10) / 10;
}

// Numeric field that allows the user to clear the value. When the input is
// empty and loses focus it falls back to the default value, so calculations
// always have a valid number to work with.
function NumericField({ label, value, defaultValue, onCommit, min, max, step, inline }) {
  const [str, setStr] = useState(String(value));
  const [focused, setFocused] = useState(false);

  // Sync local string with external value, but only when the input is not
  // focused, so we don't interrupt the user while they are typing.
  useEffect(() => {
    if (!focused) setStr(String(value));
  }, [value, focused]);

  const handleChange = (e) => setStr(e.target.value);
  const handleFocus = () => setFocused(true);
  const handleBlur = () => {
    setFocused(false);
    if (str === '' || str === null) {
      onCommit(defaultValue);
    } else {
      const num = Number(str);
      if (!isNaN(num)) {
        onCommit(num);
      } else {
        setStr(String(value));
      }
    }
  };

  const inputStyle = inline
    ? { width: 45, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.8rem', textAlign: 'center' }
    : { width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', marginTop: 3 };

  if (inline) {
    return (
      <input
        type="number"
        value={str}
        min={min}
        max={max}
        step={step}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={inputStyle}
      />
    );
  }

  return (
    <label style={{ fontSize: '0.85rem', fontWeight: 500, color: '#555' }}>
      {label}
      <input
        type="number"
        value={str}
        min={min}
        max={max}
        step={step}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={inputStyle}
      />
    </label>
  );
}

export default function EVCarConfig({ config, onChange, onClose }) {
  const [localConfig, setLocalConfig] = useState(config || getDefaultConfig());
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (config) setLocalConfig(config);
  }, [config]);

  const handleNumericCommit = useCallback((field, value) => {
    setLocalConfig(prev => {
      const next = { ...prev, [field]: value };
      // Auto-calculate dependent fields
      if (field === 'consumption' || field === 'batteryCapacity') {
        if (next.batteryCapacity > 0 && next.consumption > 0) {
          next.range = calcRange(next.batteryCapacity, next.consumption);
        }
      } else if (field === 'range') {
        if (next.batteryCapacity > 0 && next.range > 0) {
          next.consumption = calcConsumption(next.batteryCapacity, next.range);
        }
      }
      if (onChange) onChange(next);
      return next;
    });
  }, [onChange]);

  const handleReset = useCallback(() => {
    const defaults = getDefaultConfig();
    setLocalConfig(defaults);
    if (onChange) onChange(defaults);
  }, [onChange]);

  const tableLowKwh = (localConfig.batteryCapacity * localConfig.tableLow / 100) || 0;
  const tableLowKm = localConfig.consumption > 0 ? Math.round(tableLowKwh / localConfig.consumption * 100) : 0;
  const tableHighKwh = (localConfig.batteryCapacity * localConfig.tableHigh / 100) || 0;
  const tableHighKm = localConfig.consumption > 0 ? Math.round(tableHighKwh / localConfig.consumption * 100) : 0;

  if (!visible) {
    return (
      <button
        onClick={() => setVisible(true)}
        title="Configuración del vehículo eléctrico"
        style={{
          background: '#fff',
          border: '2px solid #2563eb',
          borderRadius: '50%',
          width: 38,
          height: 38,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '1.2rem',
          boxShadow: '0 2px 8px rgba(37,99,235,0.18)',
        }}
      >
        🚗
      </button>
    );
  }

  return (
    <div className="ev-car-config-panel" style={{
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={(e) => { if (e.target === e.currentTarget) setVisible(false); }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: '20px 24px', width: 360, maxHeight: '80vh', overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>🚗 Vehículo EV</h3>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={handleReset}
              title="Restablecer valores por defecto"
              style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.78rem', color: '#374151', padding: '4px 8px', cursor: 'pointer' }}
            >↺ Defaults</button>
            <button onClick={() => setVisible(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b7280' }}>✕</button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Modelo */}
          <label style={{ fontSize: '0.85rem', fontWeight: 500, color: '#555' }}>
            Modelo
            <input type="text" value={localConfig.model} disabled
              style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', background: '#f3f4f6', color: '#9ca3af', marginTop: 3 }} />
          </label>

          {/* Max charge power */}
          <NumericField
            label="Máxima potencia de carga (kW)"
            value={localConfig.maxChargePower}
            defaultValue={getDefaultConfig().maxChargePower}
            onCommit={(v) => handleNumericCommit('maxChargePower', v)}
          />

          {/* Consumption */}
          <NumericField
            label="Consumo en carretera (kWh/100km)"
            value={localConfig.consumption}
            defaultValue={getDefaultConfig().consumption}
            step="0.1"
            onCommit={(v) => handleNumericCommit('consumption', v)}
          />

          {/* Battery capacity */}
          <NumericField
            label="Capacidad neta batería (kWh)"
            value={localConfig.batteryCapacity}
            defaultValue={getDefaultConfig().batteryCapacity}
            step="0.1"
            onCommit={(v) => handleNumericCommit('batteryCapacity', v)}
          />

          {/* Range */}
          <NumericField
            label="Autonomía (km)"
            value={localConfig.range}
            defaultValue={getDefaultConfig().range}
            onCommit={(v) => handleNumericCommit('range', v)}
          />

          {/* Start battery % */}
          <NumericField
            label="% batería al inicio del viaje"
            value={localConfig.startBattery}
            defaultValue={getDefaultConfig().startBattery}
            min={10}
            max={100}
            onCommit={(v) => handleNumericCommit('startBattery', v)}
          />

          {/* Arrival battery % */}
          <NumericField
            label="% batería mínimo al llegar al destino"
            value={localConfig.arrivalBattery}
            defaultValue={getDefaultConfig().arrivalBattery}
            min={5}
            max={50}
            onCommit={(v) => handleNumericCommit('arrivalBattery', v)}
          />

          {/* Min battery stop */}
          <NumericField
            label="Mínimo % batería en paradas"
            value={localConfig.minBatteryStop}
            defaultValue={getDefaultConfig().minBatteryStop}
            min={5}
            max={50}
            onCommit={(v) => handleNumericCommit('minBatteryStop', v)}
          />

          {/* Max battery charge */}
          <NumericField
            label="Tope % al cargar"
            value={localConfig.maxBatteryCharge}
            defaultValue={getDefaultConfig().maxBatteryCharge}
            min={50}
            max={100}
            onCommit={(v) => handleNumericCommit('maxBatteryCharge', v)}
          />
        </div>

        {/* Table calculation */}
        <div style={{ marginTop: 14, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#1e293b' }}>Cálculo por niveles de batería</h4>
          <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '4px 6px' }}>%</th>
                <th style={{ textAlign: 'right', padding: '4px 6px' }}>kWh</th>
                <th style={{ textAlign: 'right', padding: '4px 6px' }}>km</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '4px 6px' }}>
                  <NumericField
                    value={localConfig.tableLow}
                    defaultValue={getDefaultConfig().tableLow}
                    onCommit={(v) => handleNumericCommit('tableLow', v)}
                    min={1}
                    max={99}
                    inline
                  /> %
                </td>
                <td style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600, color: '#2563eb' }}>{tableLowKwh.toFixed(1)}</td>
                <td style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600, color: '#059669' }}>{tableLowKm}</td>
              </tr>
              <tr>
                <td style={{ padding: '4px 6px' }}>
                  <NumericField
                    value={localConfig.tableHigh}
                    defaultValue={getDefaultConfig().tableHigh}
                    onCommit={(v) => handleNumericCommit('tableHigh', v)}
                    min={1}
                    max={100}
                    inline
                  /> %
                </td>
                <td style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600, color: '#2563eb' }}>{tableHighKwh.toFixed(1)}</td>
                <td style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600, color: '#059669' }}>{tableHighKm}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function getDefaultEVCarConfig() {
  return getDefaultConfig();
}

export function suggestChargingStops(totalDistanceKm, config) {
  if (!totalDistanceKm || !config || !config.consumption || !config.batteryCapacity) return [];
  
  const startBattery = config.startBattery ?? 100;
  const arrivalBattery = config.arrivalBattery ?? 20;
  const minBatteryStop = config.minBatteryStop ?? 20;
  const maxBatteryCharge = config.maxBatteryCharge ?? 80;
  
  // km per 1% of battery
  const kmPerPercent = config.batteryCapacity / config.consumption; // kWh per 100km -> km per kWh = 100/consumption; kmPerPercent = batteryCapacity / consumption
  
  // Range available from start until we hit minBatteryStop
  const firstLegRange = (startBattery - minBatteryStop) / 100 * config.batteryCapacity / config.consumption * 100;
  
  // Range available after each charge (from maxBatteryCharge to minBatteryStop)
  const usableRange = (maxBatteryCharge - minBatteryStop) / 100 * config.batteryCapacity / config.consumption * 100;
  
  // Range needed to arrive at destination with arrivalBattery (from minBatteryStop)
  // We need to ensure the last segment leaves at least arrivalBattery% at destination
  const arrivalReserveKm = (arrivalBattery - 0) / 100 * config.batteryCapacity / config.consumption * 100;
  
  if (usableRange <= 0) return [];
  
  // Check if we can make it without stops (considering arrival battery)
  const neededRange = totalDistanceKm;
  const availableFromStart = (startBattery - arrivalBattery) / 100 * config.batteryCapacity / config.consumption * 100;
  if (availableFromStart >= neededRange) return []; // No stops needed
  
  const stops = [];
  let distanceCovered = 0;
  
  // First leg: use the initial battery down to minBatteryStop
  let currentLegRange = firstLegRange;
  
  while (true) {
    // Can we reach the destination from here with current charge?
    const remainingDistance = totalDistanceKm - distanceCovered;
    
    // After charging at next stop, we'd have maxBatteryCharge%. 
    // But first check: can we reach destination if we stop now and charge?
    // If remaining distance after this leg can be covered arriving with arrivalBattery
    const kmNeededForArrival = remainingDistance;
    const kmAvailableAfterCharge = (maxBatteryCharge - arrivalBattery) / 100 * config.batteryCapacity / config.consumption * 100;
    
    // If we can't even finish this leg, we need to stop earlier
    if (distanceCovered + currentLegRange >= totalDistanceKm) {
      // Check if we arrive with enough battery
      const batteryAtArrival = startBattery - (totalDistanceKm / (config.batteryCapacity / config.consumption * 100)) * 100;
      break; // We can reach without more stops from current position
    }
    
    // Add stop at current leg limit
    distanceCovered += currentLegRange;
    
    // Check if after charging here we can reach destination with arrivalBattery
    const remainingAfterStop = totalDistanceKm - distanceCovered;
    if (remainingAfterStop <= kmAvailableAfterCharge) {
      stops.push(Math.round(distanceCovered));
      break; // Last stop - can reach destination with enough battery
    }
    
    stops.push(Math.round(distanceCovered));
    // After charging, range from maxBatteryCharge to minBatteryStop
    currentLegRange = usableRange;
  }
  
  return stops;
}
